import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Button } from "@tcg/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { usePos, useRegister } from "@/app/context";
import { call } from "@/lib/api";
import type { QuickItem, SearchRow } from "@/lib/types";
import { useAvailabilityStore } from "@/realtime/availability";

import { useCart } from "./hooks/useCart";
import { liveTile } from "./liveTile";
import { ResultTile, type TileItem } from "./ResultTile";

type Panel = "search" | "sealed" | "supplies";

function fromSearch(row: SearchRow): TileItem {
  return {
    variantId: row.variantId,
    title: row.title,
    variantTitle: row.variantTitle ?? null,
    productType: row.productType,
    rawRank: row.rawRank ?? null,
    grading: row.grading ?? null,
    certNumber: row.certNumber ?? null,
    price: row.price.amount,
    available: row.available,
    quantity: row.quantity,
    reason: row.reason ?? null,
    isConsigned: row.isConsigned,
    thumb: row.thumb ?? null,
  };
}

function fromQuick(row: QuickItem): TileItem {
  return {
    variantId: row.variantId,
    title: row.label,
    productType: row.productType,
    rawRank: row.rawRank ?? null,
    price: row.price.amount,
    available: row.available,
    quantity: row.quantity,
    isConsigned: false,
    thumb: row.thumb ?? null,
  };
}

/** Left pane (design/05 §3.1): search results or quick buttons, then the row of quick buttons. */
export function SearchPane({ query, onAdd }: { query: string; onAdd: (item: TileItem) => void }) {
  const { t } = useTranslation();
  const { api } = usePos();
  const register = useRegister();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<Panel>("search");
  const q = query.trim();

  const search = useQuery({
    queryKey: ["pos-search", "list", q],
    queryFn: () => call(() => api.GET("/v1/pos/search", { params: { query: { q, limit: 24 } } })),
    enabled: q.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5_000,
  });
  const quick = useQuery({
    queryKey: ["pos-quick-items", "list"],
    queryFn: () => call(() => api.GET("/v1/pos/quick-items")),
    enabled: panel !== "search",
    staleTime: 30_000,
  });

  // R21: the location's availability stream (one subscription for the session, in the layout)
  const availability = useAvailabilityStore();
  const checking = availability.checking();
  const basket = useCart().cart;
  const inBasket = new Set((basket?.lines ?? []).map((line) => line.variantId ?? ""));

  const showSearch = q.length > 0 || panel === "search";
  const readAt = showSearch ? search.dataUpdatedAt : quick.dataUpdatedAt;
  const tiles: TileItem[] = showSearch
    ? q
      ? (search.data?.data ?? []).map(fromSearch)
      : []
    : (quick.data?.data ?? [])
        .filter((row) =>
          panel === "sealed" ? row.productType === "sealed" : row.productType !== "sealed",
        )
        .map(fromQuick);

  return (
    <section aria-labelledby="results-title" className="flex min-h-0 flex-col gap-3 p-3">
      <h2 id="results-title" className="text-sm font-medium text-muted-foreground">
        {t("sale.results_title")}
      </h2>
      <div className="min-h-0 flex-1 overflow-y-auto" aria-busy={search.isFetching}>
        {q && search.isFetching && !search.data ? (
          <p className="p-6 text-center text-muted-foreground">{t("sale.searching")}</p>
        ) : null}
        {q && search.data?.mode === "simple" ? (
          <p
            role="status"
            data-testid="search-simple"
            className="mb-2 rounded-md border border-status-pending px-3 py-2 text-base"
          >
            {t("sale.simple_search")}
          </p>
        ) : null}
        {q && search.data && tiles.length === 0 ? (
          <p className="p-6 text-center text-muted-foreground">{t("sale.no_results", { q })}</p>
        ) : null}
        {!q && showSearch ? (
          <div className="flex h-full items-center justify-center rounded-md border border-dashed p-6 text-center text-lg text-muted-foreground">
            {t("sale.results_empty")}
          </div>
        ) : null}
        <ul className="grid grid-cols-1 gap-2 xl:grid-cols-2">
          {tiles
            .map((item) => liveTile(item, availability, readAt, checking, inBasket))
            .map(({ item, goneAt, checking: wait }) => (
              <li key={item.variantId}>
                <ResultTile item={item} onAdd={onAdd} goneAt={goneAt} checking={wait} />
              </li>
            ))}
        </ul>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Button
          variant={panel === "sealed" && !q ? "default" : "secondary"}
          size="pos"
          aria-pressed={panel === "sealed"}
          onClick={() => setPanel(panel === "sealed" ? "search" : "sealed")}
        >
          {t("sale.quick_sealed")}
        </Button>
        <Button
          variant={panel === "supplies" && !q ? "default" : "secondary"}
          size="pos"
          aria-pressed={panel === "supplies"}
          onClick={() => setPanel(panel === "supplies" ? "search" : "supplies")}
        >
          {t("sale.quick_supplies")}
        </Button>
        <Button variant="secondary" size="pos" onClick={() => void navigate("/buylist")}>
          {t("sale.quick_buylist")}
        </Button>
        <Button
          variant="secondary"
          size="pos"
          onClick={() => void navigate("/consignment")}
          disabled={!register.data}
        >
          {t("sale.quick_consignment")}
        </Button>
      </div>
    </section>
  );
}
